import "./App.css";
import { Suspense, lazy, useCallback, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { AuthProvider } from "./common/auth/AuthContext";
import { FavouritesProvider } from "./common/favourites/FavouritesContext";
import { LearningProvider } from "./common/learning/LearningContext";
import { BROWSE_NAV, PRIMARY_NAV, ROUTES, toPlayer } from "./common/navigation";
import useAuth from "./hooks/useAuth";
import useBrowseMode from "./hooks/useBrowseMode";
import useLearning from "./hooks/useLearning";
import { SeasonSelectionProvider } from "./hooks/useSeasonSelection";
import { SourceNoticeProvider } from "./common/sourceNotice/SourceNoticeContext";
import BrowsingBanner from "./components/common/BrowsingBanner/BrowsingBanner";
import IQToast from "./components/common/IQToast/IQToast";

import AppBoot from "./components/common/AppBoot/AppBoot";
import BottomNav from "./components/common/BottomNav/BottomNav";
import ErrorBoundary from "./components/common/ErrorBoundary/ErrorBoundary";
import Footer from "./components/common/Footer/Footer";
import IQPill from "./components/common/IQPill/IQPill";
import LoadingState from "./components/common/LoadingState/LoadingState";
import LocalModeBanner from "./components/common/LocalModeBanner/LocalModeBanner";
import MobileNavBar from "./components/common/MobileNavBar/MobileNavBar";
import NavBar from "./components/common/NavBar/NavBar";
import RouteAnalytics from "./components/common/RouteAnalytics/RouteAnalytics";
import RouteSeo from "./components/common/RouteSeo/RouteSeo";
import SaveIssueBanner from "./components/common/SaveIssueBanner/SaveIssueBanner";
import ScrollToTop from "./components/common/ScrollToTop/ScrollToTop";
import SeasonNavControl from "./components/common/SeasonNavControl/SeasonNavControl";

/* Eager: the screens a session can start on. Landing is what a stranger
   arriving from a search result sees first, so it must not cost a second
   round trip. */
import Four04 from "./components/pages/Four04/Four04";
import Home from "./components/pages/Home/Home";
import Landing from "./components/pages/Landing/Landing";
import Welcome from "./components/pages/Welcome/Welcome";

/* Lazy: everything reached by a deliberate tap. Splitting these keeps the
   first paint small on a phone connection (PRD §40).

   The importers are named rather than inlined so they can also be called
   ahead of time. See PREFETCH_ON_IDLE below. */
const importConcept = () => import("./components/pages/Concept/Concept");
const importGame = () => import("./components/pages/Game/Game");
const importGameRoom = () => import("./components/pages/GameRoom/GameRoom");
const importGames = () => import("./components/pages/Games/Games");
const importLearn = () => import("./components/pages/Learn/Learn");
const importPlayer = () => import("./components/pages/Player/Player");

const Concept = lazy(importConcept);
const Game = lazy(importGame);
const GameRoom = lazy(importGameRoom);
const Games = lazy(importGames);
const Learn = lazy(importLearn);
const Player = lazy(importPlayer);
const importPlayers = () => import("./components/pages/Players/Players");
const Players = lazy(importPlayers);

/*
 * The tab bar's browsable destinations, fetched once the app is idle.
 *
 * Code splitting keeps the first paint small, and charges for it on the
 * first tap of every tab: the chunk is requested only when the route
 * renders, so on a phone connection each new tab pauses on a spinner
 * before it can draw anything. That reads as the app being slow, when it
 * is actually the network.
 *
 * Warming them after first paint inside the app moves that cost to a moment
 * when nobody is waiting. It is only a prefetch: webpack caches the module,
 * so the later lazy() resolves from memory instead of the network.
 *
 * Deliberately not everything. A game, a room, a concept and a player are
 * all reached from inside one of these, by which point their own chunk
 * has had time to arrive.
 */
const PREFETCH_ON_APP_IDLE = [importGames, importLearn, importPlayers];
const PREFETCH_BY_ROUTE = new Map([
  [ROUTES.GAMES, importGames],
  [ROUTES.LEARN, importLearn],
  [ROUTES.TEAMS, importPlayers],
]);
const prefetchedLoaders = new Set();

function prefetchLoaders(loaders) {
  loaders.forEach((load) => {
    if (prefetchedLoaders.has(load)) return;
    prefetchedLoaders.add(load);
    load().catch(() => {
      prefetchedLoaders.delete(load);
    });
  });
}

function prefetchRoute(to) {
  const load = PREFETCH_BY_ROUTE.get(to);
  if (load) prefetchLoaders([load]);
}

function usePrefetchRoutes() {
  const { status } = useAuth();
  const { browsing } = useBrowseMode();
  const { pathname } = useLocation();
  const shouldPrefetch =
    !DOORWAY_ROUTES.has(pathname) && (status === "signed-in" || browsing);

  useEffect(() => {
    if (!shouldPrefetch) return undefined;

    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      // Failures are ignored on purpose: this is an optimisation, and the
      // real navigation will request the chunk again and surface any error
      // properly through Suspense.
      prefetchLoaders(PREFETCH_ON_APP_IDLE);
    };

    // requestIdleCallback is unavailable in Safari, where a short timeout is
    // close enough: the point is only "after the first paint has settled".
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(warm, { timeout: 3000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(id);
      };
    }

    const id = window.setTimeout(warm, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [shouldPrefetch]);
}

function useRoutePrefetchIntent() {
  return useCallback((to) => {
    prefetchRoute(to);
  }, []);
}
const Profile = lazy(() => import("./components/pages/Profile/Profile"));
const Settings = lazy(() => import("./components/pages/Settings/Settings"));
const StyleGuide = lazy(() => import("./components/pages/StyleGuide/StyleGuide"));
const Privacy = lazy(() => import("./components/pages/Legal/Privacy"));
const Terms = lazy(() => import("./components/pages/Legal/Terms"));

/**
 * `/` is auth-aware (PRD §9).
 *
 * With a session it is still a gate and sends you straight to Home: a
 * returning user has no use for the sales pitch. Without one it serves the
 * marketing landing page, because `/` is the URL that gets shared, linked
 * and indexed. Signing in remains `/welcome`'s job.
 */
function RootGate() {
  const { status, authReady, backendMode, ensureAuthReady } = useAuth();
  const onRouteIntent = useRoutePrefetchIntent();

  useEffect(() => {
    if (authReady || status === "signed-in") return undefined;

    let cancelled = false;
    const warm = () => {
      if (!cancelled) ensureAuthReady();
    };

    const delay = backendMode.mode === "firebase" ? 3000 : 0;
    const id = window.setTimeout(warm, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [authReady, backendMode.mode, ensureAuthReady, status]);

  if (status === "signed-in") return <Navigate to={ROUTES.HOME} replace />;
  return <Landing onBrowseIntent={() => onRouteIntent(ROUTES.GAMES)} />;
}

/**
 * A route that needs an account, unless it is one a visitor may read.
 *
 * `browsable` marks the public football content: the schedule, a game, the
 * lessons, the clubs. Those render for someone who chose to look around
 * without signing in. Everything else, anything personal or shared or
 * written, still redirects.
 *
 * Browsing is deliberately not a weaker session. There is no uid behind
 * it, so every write path already fails closed without knowing this
 * component exists.
 */
function RequireAuth({ children, browsable = false }) {
  const { status, authReady, ensureAuthReady } = useAuth();
  const { browsing } = useBrowseMode();
  const location = useLocation();

  useEffect(() => {
    if (authReady || (browsable && browsing && status !== "signed-in")) {
      return undefined;
    }

    ensureAuthReady();
    return undefined;
  }, [authReady, browsable, browsing, ensureAuthReady, status]);

  if (browsable && browsing && status !== "signed-in") return children;

  if (!authReady || status === "initializing") {
    return <LoadingState title="Getting the game ready…" />;
  }
  if (status !== "signed-in") {
    if (browsable && browsing) return children;
    return <Navigate to={ROUTES.WELCOME} replace state={{ from: location }} />;
  }
  return children;
}

/*
 * `/` and `/welcome` are the doors into the app, not rooms inside it.
 *
 * The landing page is the pitch for someone who has not decided yet. It
 * has its own calls to action, and a browsing visitor was getting an app
 * tab bar pinned under the marketing copy, which read as a rendering
 * fault. `/welcome` is the same argument: it exists to get you an
 * account, and offering tabs away from it works against that.
 *
 * This is only reachable while signed out or browsing. A signed-in
 * visitor never sees either screen, because RootGate sends them to Home.
 */
const DOORWAY_ROUTES = new Set([ROUTES.ROOT, ROUTES.WELCOME]);

function useIsDoorway() {
  const { pathname } = useLocation();
  return DOORWAY_ROUTES.has(pathname);
}

function useSeasonSelectionEnabled() {
  const { pathname, search } = useLocation();
  const { status } = useAuth();
  const { browsing } = useBrowseMode();

  if (status !== "signed-in" && !browsing) return false;

  if (pathname === ROUTES.GAMES) return true;
  if (pathname !== ROUTES.TEAMS) return false;

  const params = new URLSearchParams(search);
  return Boolean((params.get("team") ?? "").trim());
}

function AppHeader() {
  const { profile, status } = useAuth();
  const { browsing } = useBrowseMode();
  const isDoorway = useIsDoorway();
  const onRouteIntent = useRoutePrefetchIntent();
  const pill =
    status === "signed-in" && profile ? (
      <IQPill alias={profile.alias} iq={profile.footballIQ} />
    ) : null;

  /* Signed in: everything. Browsing: only what a visitor can actually
     open, so the bar never offers a tab that bounces them to /welcome.
     Neither: nothing, because the landing page is the whole screen. */
  const links = isDoorway
    ? []
    : status === "signed-in"
      ? PRIMARY_NAV
      : browsing
        ? BROWSE_NAV
        : [];

  return (
    <>
      <NavBar links={links} onLinkIntent={onRouteIntent}>
        <SeasonNavControl variant="desktop" />
        {pill}
      </NavBar>
      <MobileNavBar>
        <SeasonNavControl variant="mobile" />
        {profile ? (
          <IQPill alias={profile.alias} iq={profile.footballIQ} compact />
        ) : null}
      </MobileNavBar>
    </>
  );
}

/** `/learn/players/:playerId` kept working, at its new address. */
function LegacyPlayerRedirect() {
  const { playerId } = useParams();
  return <Navigate to={toPlayer(playerId)} replace />;
}

function BottomNavForSession() {
  const { status } = useAuth();
  const { browsing } = useBrowseMode();
  const isDoorway = useIsDoorway();
  const onRouteIntent = useRoutePrefetchIntent();

  if (isDoorway) return null;
  if (status === "signed-in") return <BottomNav onLinkIntent={onRouteIntent} />;
  if (browsing) {
    return <BottomNav links={BROWSE_NAV} onLinkIntent={onRouteIntent} />;
  }
  return null;
}

/* The browsing banner is app chrome as much as the tab bar is, and the
   landing page already makes the same offer, at greater length. */
function LayoutChrome() {
  return useIsDoorway() ? null : <BrowsingBanner />;
}

function Layout() {
  const { lastAward, clearLastAward } = useLearning();
  const seasonSelectionEnabled = useSeasonSelectionEnabled();
  usePrefetchRoutes();

  return (
    <SeasonSelectionProvider enabled={seasonSelectionEnabled}>
      <div className="AppShell">
        <a className="app-skip-link" href="#main-content">
          Skip to content
        </a>

        <LocalModeBanner />
        <SaveIssueBanner />
        <LayoutChrome />
        <AppHeader />

        <ScrollToTop />
        <RouteSeo />
        <RouteAnalytics />

        <main id="main-content" className="AppMain">
          <ErrorBoundary>
            <Suspense fallback={<LoadingState title="One moment…" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>

        <Footer />

        <BottomNavForSession />

        <IQToast award={lastAward} onDismiss={clearLastAward} />
      </div>
    </SeasonSelectionProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path={ROUTES.ROOT} element={<RootGate />} />
        <Route path={ROUTES.WELCOME} element={<Welcome />} />

        <Route
          path={ROUTES.HOME}
          element={
            <RequireAuth>
              <Home />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.GAMES}
          element={
            <RequireAuth browsable>
              <Games />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.GAME}
          element={
            <RequireAuth browsable>
              <Game />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.ROOM}
          element={
            <RequireAuth>
              <GameRoom />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.LEARN}
          element={
            <RequireAuth browsable>
              <Learn />
            </RequireAuth>
          }
        />
        {/* Both of these are literal-segment paths under /learn, so the
            router ranks them above /learn/:conceptId whatever order they
            are declared in. Declared first anyway, so a reader does not
            have to know that. */}
        <Route
          path={ROUTES.TEAMS}
          element={
            <RequireAuth browsable>
              <Players />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.PLAYER}
          element={
            <RequireAuth browsable>
              <Player />
            </RequireAuth>
          }
        />

        {/* Clubs and players used to live under /learn. Those URLs have
            been shared, so they redirect rather than 404. The player one
            keeps its id: `/learn/players/x` becomes `/teams/players/x`. */}
        <Route
          path={ROUTES.LEGACY_PLAYERS}
          element={<Navigate to={ROUTES.TEAMS} replace />}
        />
        <Route path={ROUTES.LEGACY_PLAYER} element={<LegacyPlayerRedirect />} />
        <Route
          path={ROUTES.CONCEPT}
          element={
            <RequireAuth browsable>
              <Concept />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <RequireAuth>
              <Profile />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />

        <Route path={ROUTES.PRIVACY} element={<Privacy />} />
        <Route path={ROUTES.TERMS} element={<Terms />} />
        <Route path={ROUTES.STYLE_GUIDE} element={<StyleGuide />} />
        <Route path={ROUTES.NOT_FOUND} element={<Four04 />} />
        <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <FavouritesProvider>
        <LearningProvider>
          <SourceNoticeProvider>
            <AppBoot>
              <AppRoutes />
            </AppBoot>
          </SourceNoticeProvider>
        </LearningProvider>
      </FavouritesProvider>
    </AuthProvider>
  );
}

export { ROUTES };
export default App;
